import { HttpClient } from '@angular/common/http';
import {
  EnvironmentProviders,
  inject,
  Injectable,
  isDevMode,
  makeEnvironmentProviders,
} from '@angular/core';
import {
  Translation,
  TranslocoLoader,
  provideTransloco,
} from '@jsverse/transloco';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly _http = inject(HttpClient);

  getTranslation(lang: string): Observable<Translation> {
    return this._http.get<Translation>(`/assets/i18n/${lang}.json`);
  }
}

export const DEFAULT_LANGUAGE = 'en';

export const AVAILABLE_LANGUAGES = [
  // Quality certified on backend
  'en', // English
  'es', // Spanish
  'fr', // French
  'de', // German
  'it', // Italian
  'pt', // Portuguese
  'zh', // Chinese
  'ja', // Japanese
  'ko', // Korean
  'ar', // Arabic
  'nl', // Dutch
  'ru', // Russian
  'hi', // Hindi
  'sv', // Swedish
  'no', // Norwegian
  'da', // Danish
  'el', // Greek
  // Not yet quality certified on backend
  'pl', // Polish
  'tr', // Turkish
  'vi', // Vietnamese
  'th', // Thai
  'id', // Indonesian
  'ms', // Malay
  'he', // Hebrew
  // Locale variants + additional languages with i18n files
  'fr-ca', // French Canadian
  'en-au', // Australian English
  'cs', // Czech
  'hr', // Croatian
  'hu', // Hungarian
  'sk', // Slovak
  'sl', // Slovenian
  'fi', // Finnish
];

/**
 * Resolve the best-matching Transloco active lang (i18n UI strings only) for a
 * selected/event language code. Region-aware codes that have a dedicated i18n
 * file (e.g. `fr-ca`, `en-au`) resolve to that file; otherwise the region is
 * stripped to the base subtag so a regional code like `tr-TR` falls back to the
 * short-ISO file `tr`. Falls back to {@link DEFAULT_LANGUAGE} when neither exists.
 *
 * NOTE: This is ONLY for selecting the Transloco active lang. It does NOT change
 * the canonical language key used by the translation/insights pipeline
 * (see `@utils/language-code.ts`), where the region must be preserved.
 */
export function resolveTranslocoLang(code: string | undefined | null): string {
  if (!code) return DEFAULT_LANGUAGE;
  const normalized = code.toLowerCase();
  const langs = AVAILABLE_LANGUAGES as readonly string[];
  if (langs.includes(normalized)) {
    return normalized;
  }
  const base = normalized.split('-')[0];
  if (langs.includes(base)) {
    return base;
  }
  return DEFAULT_LANGUAGE;
}

export function provideTranslocoConfig(): EnvironmentProviders {
  return makeEnvironmentProviders(
    provideTransloco({
      config: {
        availableLangs: AVAILABLE_LANGUAGES,
        defaultLang: DEFAULT_LANGUAGE,
        fallbackLang: DEFAULT_LANGUAGE,
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
        missingHandler: {
          useFallbackTranslation: true,
        },
      },
      loader: TranslocoHttpLoader,
    })
  );
}
